import { Cameraswitch } from '@mui/icons-material';
import { Button } from '@thedealersconcierge/components';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import ScanbotSDK from 'scanbot-web-sdk';
import { Image, ImageRotation } from 'scanbot-web-sdk/@types/core-types';
import { IDocumentScannerHandle } from 'scanbot-web-sdk/@types/interfaces/i-document-scanner-handle';
import {
  DocumentScannerViewConfiguration,
  TextConfiguration
} from 'scanbot-web-sdk/@types/model/configuration/document-scanner-view-configuration';
import { getScanbotSdk } from '~/lib/scanbot-client';

type Props = {
  text: TextConfiguration;
  onNewPage: (d: Image) => void;
  aspectRatio?: ConstrainDouble;
  enableManualCapture?: boolean;
};

export default function Scanner({
  text,
  onNewPage,
  aspectRatio,
  enableManualCapture
}: Props) {
  const scannerRef = useRef<IDocumentScannerHandle | null>(null);
  const [canFlipCamera, setCanFlipCamera] = useState(false);

  const vc: MediaTrackConstraints = {
    /**
     * Docs: https://api-docs.scanbot.io/web/types/CameraFacingMode.html
     * CameraFacingMode: "front" | "back" | "unknown"
     */
    facingMode: { exact: 'environment' },

    aspectRatio: aspectRatio,

    width: { ideal: 1280 },
    height: { ideal: 960 }
  };

  const scannerConfig: DocumentScannerViewConfiguration = {
    containerId: 'document-scanner-container',
    text,
    videoConstraints: {
      /**
       * Docs: https://api-docs.scanbot.io/web/types/CameraFacingMode.html
       * CameraFacingMode: "front" | "back" | "unknown"
       */
      facingMode: 'back', // Always set to back camera when available
      aspectRatio
    },

    onDocumentDetected: (result) => {
      // This is on the perimeter of the library that does not support typing
      // so we add on our own types
      if (result.croppedImage) {
        onNewPage(result.croppedImage);
      }
    },
    onCaptureButtonClick: async () => {
      if (scannerRef.current) {
        const captured = await scannerRef.current.detectAndCrop();

        // When a cropped image detected we use the cropped image
        if (captured?.croppedImage) {
          onNewPage(captured.croppedImage);
          return;
        }

        // If no cropped image detected then use the original image
        if (captured && enableManualCapture) {
          onNewPage(captured.originalImage);
        }
      }
    }
  };
  /**
   * There is 2 Main ways to flip camera:
   * 1. Manually switch between  devices
   *  docs: https://docs.scanbot.io/document-scanner-sdk/web/features/document-scanner/classic-ui/#switching-to-a-specific-available-camera
   *
   * 2. Use swapCameraFacing to swap directly between back and forth facing
   *  docs: https://docs.scanbot.io/barcode-scanner-sdk/web/barcode-scanner/classic-scanner-ui/#switching-between-the-front-and-rear-camera
   */
  const flipCamera = async () => {
    if (!scannerRef.current) {
      toast.error('Unable flip camera, scanner is not loaded');
      return;
    }

    try {
      // Passing "true" will tell the SDK will tear down the current stream and immediately request the opposite camera
      scannerRef.current.swapCameraFacing(true);
      return;
    } catch (e) {
      toast.error('Unable to flip camera');
    }
  };

  // Initialize scanbot in this use effect
  useEffect(() => {
    getScanbotSdk()
      .then((sbsdk) => sbsdk.createDocumentScanner(scannerConfig))
      .then((scanner) => {
        if (scannerRef.current !== null) {
          return;
        }
        scannerRef.current = scanner;
      })
      // Check if we can Flip Camera
      .then(async () => {
        if (!scannerRef.current) {
          return;
        }

        const cameras = await scannerRef.current.fetchAvailableCameras();
        if (cameras.length > 1) {
          setCanFlipCamera(true);
        }
      })
      .catch(console.error);

    return () => {
      scannerRef.current?.dispose();
    };
  }, []);

  return (
    <div className="w-full h-full relative">
      {canFlipCamera && (
        <Button
          onClick={flipCamera}
          size="MEDIUM"
          iconLeft={<Cameraswitch />}
          className="absolute top-4 left-4 z-modal"
        />
      )}
      <div id="document-scanner-container" className="w-full h-full" />
    </div>
  );
}

/**
 * Shared functionalities to work with a Scanned documents
 */

// Apply Contrast to Image
export const applyContrast = async (
  sdk: ScanbotSDK,
  image: Image
): Promise<Image> => {
  const filter = new ScanbotSDK.Config.ContrastFilter({
    contrast: 1
  });
  return await sdk.imageFilter(image, filter);
};

// Apply Correct Rotation to Image
export const applyCorrectRotation = async (
  sdk: ScanbotSDK,
  image: Image
): Promise<Image> => {
  /**
   * Capture the Image Rotation with image anaylzer
   *
   * This can detect Upside down image as well!
   */
  const analyzer = await sdk.createDocumentQualityAnalyzer({
    detectOrientation: true // We are only interested in it's rotation right now
  });
  const imageAnalysis = await analyzer.analyze(image);

  // Will trigger when orientation / document is not detected found
  // This can happen when the image is manually captured without document detected
  if (imageAnalysis.orientation === null || !imageAnalysis.documentFound) {
    const result = await sdk.imageToJpeg(image);
    return result;
  }

  /**
   * Map the detected document orientation to the correct rotation
   *
   * This is done to make a more robust orientation system so we don't rely solely on the number enums (90, -90, -180)
   */
  let rotation: ImageRotation = 'NONE';
  if (imageAnalysis.orientation >= -45 && imageAnalysis.orientation <= 45) {
    rotation = 'NONE';
  }

  if (imageAnalysis.orientation > 45 && imageAnalysis.orientation <= 135) {
    rotation = 'CLOCKWISE_90';
  }

  if (imageAnalysis.orientation < -45 && imageAnalysis.orientation >= -135) {
    rotation = 'COUNTERCLOCKWISE_90';
  }

  // Upside-down
  if (imageAnalysis.orientation > 135 || imageAnalysis.orientation < -135) {
    rotation = 'CLOCKWISE_180';
  }

  return await sdk.imageRotate(image, rotation);
};
